import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Button,
  Rating,
  Divider,
  TextField,
  Tabs,
  Tab,
  Paper,
  Skeleton,
  Container,
  IconButton, Dialog, DialogContent
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MailIcon from "@mui/icons-material/Mail";
import LanguageIcon from "@mui/icons-material/Language";
import CallIcon from "@mui/icons-material/Call";
import StarIcon from "@mui/icons-material/Star";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import TelegramIcon from "@mui/icons-material/Telegram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from '@mui/icons-material/YouTube';
import XIcon from "@mui/icons-material/X";
import { get_material_detailview, material_review, get_profile } from "../../api/ControllApi";
import { toast } from "react-toastify";
import { useModal } from "../../context/ModalContext";
import { useUser } from "../../context/LoginDetail";
import BreadcrumbsNav from "../../components/common/BreadCrumbNav";
import { useParams, useLocation } from "react-router-dom";
import ProductQuoteDialouge from "./ProductQuoteDialouge";
import ProductShopCard from "./ProductShopCard";
import MaterialPhotos from "./MaterialPhotos";
import MaterialService from "./MaterialService";
import AboutShop from "../Material Store/AboutShop";
import ReviewRating from "../Material Store/ReviewRating";
import { ASSET_BASE } from "../../utils/baseUrls";

export default function MaterialProductCardDetail() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [tab, setTab] = useState(0);
  const [cardRating, setCardRating] = useState(0);
  const [userRating, setUserRating] = useState(0);
  const [ratingDescription, setRatingDescription] = useState("");
  const [materialList, setMaterialList] = useState([]);
  const [materialServiceList, setMaterialServiceList] = useState([]);
  const [brands, setBrands] = useState([]);
  const { requireLogin } = useModal();
  const { user } = useUser();
  const { id } = useParams();
  const location = useLocation();

  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryContext, setEnquiryContext] = useState(null);

  const activeMaterials = materialList?.filter(item => item.is_deleted === 0) || [];
  const activeServices = materialServiceList?.filter(item => item.is_deleted === 0) || [];

  const hasProducts = activeMaterials.length > 0;
  const hasServices = activeServices.length > 0;
  const hasBrands = brands?.length > 0;

  const tabs = [];
  if (hasProducts) tabs.push("Product List");
  if (hasServices) tabs.push("Service");
  tabs.push("About Shop");
  tabs.push("Rating & Review");
  tabs.push("Photos");
  if (hasBrands) tabs.push("Brand");

  const currentTabLabel = tabs[tab] || tabs[0];

  const BorderedStar = (props) => (
    <StarIcon
      {...props}
      sx={{
        border: "1px solid #999",
        borderRadius: "6px",
        p: { xs: "2px", sm: "3px" },
        fontSize: { xs: 22, sm: 24, md: 26 },
        background: "#fff",
        mr: { xs: 0.5, sm: 0.8 },
      }}
    />
  );

  const RatingStar = (props) => (
    <StarIcon
      {...props}
      sx={{
        border: "1px solid #FEB821",
        borderRadius: "6px",
        p: "3px",
        fontSize: 26,
        background: "#fff",
        mr: 1.8,
      }}
    />
  );

  const [open, setOpen] = useState(false);
  const handleOpenReviewPopup = () => {
    requireLogin(() => {
      setOpen(true);
    });
  };

  const handleClose = () => {
    setUserRating(0);
    setRatingDescription("");
    setOpen(false);
  };

  const handleQuery = async () => {
    if (!userRating) {
      toast.error("Please give rating");
      return;
    }

    if (!ratingDescription.trim()) {
      toast.error("Please write a review");
      return;
    }

    setLoading(true);

    try {
      const res = await material_review({
        vendor_id: data?.user_id,
        rating: userRating,
        review: ratingDescription,
      });

      if (res?.status === true) {
        toast.success("Review submitted");
        setRatingDescription("");
        setUserRating(0);
        handleClose();
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };




  useEffect(() => {
    setLoading(true);
    get_material_detailview(id)
      .then((res) => {
        const vendorData = res?.data || {};
        setData(vendorData);
        
        const materials = vendorData.vendor_materials || [];
        setMaterialList(materials);
        setMaterialServiceList(vendorData.vendor_services || []);
        
        // Extract brands from material.brand array in vendor_materials
        const allBrands = materials.reduce((acc, item) => {
          const materialBrands = item?.material?.brand || [];
          if (Array.isArray(materialBrands)) {
            return [...acc, ...materialBrands];
          }
          return acc;
        }, []);

        // Unique brands by ID
        const uniqueBrands = Array.from(new Map(allBrands.map(b => [b.id, b])).values());
        setBrands(uniqueBrands);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mName = searchParams.get("materialName");
    const mId = searchParams.get("materialId");

    if (mId && !loading && Object.keys(data).length > 0) {
      // Find specific material info
      const specificMaterial = materialList?.find(
        (item) => String(item.material?.id) === String(mId)
      )?.material;

      const timer = setTimeout(() => {
        setEnquiryContext({
          name: specificMaterial?.material_name || mName,
          id: mId,
          image: specificMaterial?.gallery || specificMaterial?.image || vendor?.image,
          description: specificMaterial?.description || vendor?.description
        });
        setEnquiryOpen(true);
      }, );
      return () => clearTimeout(timer);
    }
  }, [location.search, loading, data, materialList]);

  // Robust vendor data extraction
  const vendor = Array.isArray(data) ? data[0] : data;
  const avgRating = Number(vendor?.reviews_avg_rating || 0);

  const maskPhone = (phoneStr) => {
    if (!phoneStr) return "";
    const str = String(phoneStr).trim();
    if (str.length <= 4) return str;
    const first2 = str.slice(0, 2);
    const last2 = str.slice(-2);
    const middleMask = "X".repeat(str.length - 4);
    return `${first2}${middleMask}${last2}`;
  };

  const maskEmail = (emailStr) => {
    if (!emailStr) return "";
    const str = String(emailStr).trim();
    const atIndex = str.indexOf("@");
    if (atIndex === -1) {
      if (str.length <= 4) return str;
      return str.slice(0, 2) + "X".repeat(str.length - 4) + str.slice(-2);
    }
    const localPart = str.slice(0, atIndex);
    const domainPart = str.slice(atIndex);
    if (localPart.length <= 4) {
      return localPart + domainPart;
    }
    const first2 = localPart.slice(0, 2);
    const last2 = localPart.slice(-2);
    const middleMask = "X".repeat(localPart.length - 4);
    return `${first2}${middleMask}${last2}${domainPart}`;
  };

  // open social link
  const openLink = (url) => {
    if (!url) return;

    const finalUrl = url.startsWith("http")
      ? url
      : `https://${url}`;

    window.open(finalUrl, "_blank", "noopener,noreferrer");
  };

  // social media links
  const socialLinks = [
    {
      icon: WhatsAppIcon,
      url: vendor?.whatsapp
        ? `https:wa.me/${vendor.whatsapp.replace(/\D/g, "")}`
        : null,
    },
    {
      icon: InstagramIcon,
      url: vendor?.instagram,
    },
    {
      icon: XIcon,
      url: vendor?.x,
    },
    {
      icon: FacebookIcon,
      url: vendor?.facebook,
    },
    {
      icon: LinkedInIcon,
      url: vendor?.linkedin,
    },
    {
      icon: TelegramIcon,
      url: vendor?.telegram,
    },
    {
      icon: YouTubeIcon,
      url: vendor?.youtube,
    },
  ];

  return (
    <Container maxWidth={false} sx={{ width: { xs: '95%', md: '80%' }, mx: 'auto', py: 5 }}>
      <Box sx={{ mb: 3, mt: 3 }}>
        <BreadcrumbsNav />
      </Box>
   <Card
  sx={{
    height: "max-content",
    background: "#FBFAF7",
    border: "none",
    borderRadius: "24px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
    p: { xs: 1.5, sm: 2 },
  }}
>
  <CardContent
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        lg: "auto 1fr 1fr",
      },
      gap: { xs: 2, lg: 3 },
      p: 2,
    }}
  >
    {/* IMAGE */}
    <Box
      sx={{
        width: { xs: "100%", md: 300, lg: 180 },
        maxWidth: 350,
        height: 180,
        mx: { xs: "auto", lg: 0 },
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #95B160",
      }}
    >
      {loading ? (
        <Skeleton variant="rectangular" width="100%" height="100%" />
      ) : (
        <img
          src={(() => {
            if (vendor?.thumbnail) {
              return vendor.thumbnail.startsWith("http")
                ? vendor.thumbnail
                : `${ASSET_BASE}storage/${vendor.thumbnail}`;
            }
            if (Array.isArray(vendor?.image) && vendor.image.length > 0) {
              return vendor.image[0];
            }
            return "/assets/images/placeholder_img.png";
          })()}
          alt="shop"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
    </Box>

    {/* CONTENT */}
    <Box display="flex" flexDirection="column" gap={1.25}>
      {loading ? (
        <Skeleton width={180} height={24} />
      ) : (
        <Typography fontWeight={600} fontSize={16}>
          {vendor?.name || "Material Store"}
        </Typography>
      )}

      <Box display="flex" alignItems="center" flexWrap="wrap" gap={{ xs: 2, sm: 3 }}>
        {loading ? (
          <>
            <Skeleton width={80} height={20} />
            <Skeleton width={120} height={18} />
            <Skeleton width={90} height={18} />
          </>
        ) : (
          <>
            <Rating
              value={avgRating}
              precision={0.5}
              readOnly
              size="small"
              icon={<StarIcon fontSize="inherit" />}
            />

            <Typography fontSize={13} color="text.secondary">
              {avgRating > 0 ? avgRating.toFixed(1) : "No ratings yet"} (
              {vendor?.reviews_count || 0} Reviews)
            </Typography>

            <Box display="flex" alignItems="center" gap={0.5}>
              <CallIcon fontSize="small" sx={{ color: "#6BAF4B" }} />
              <Typography
                fontSize={13}
                sx={{
                  color: "#6BAF4B",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {maskPhone(vendor?.phone) || "-"}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 2, sm: 3 },
          flexWrap: "wrap",
          mt: 0.5,
        }}
      >
        {/* {loading ? (
          <Skeleton width={200} height={28} />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
             
            }}
          >
            <LocationOnIcon fontSize="small" color="primary" />
            <Typography fontSize={12}>
              {vendor?.address || "Chennai, Tamil Nadu"}
            </Typography>
          </Box>
        )} */}

        <Box sx={{ display: "flex", gap: 0.6 }}>
          {loading
            ? [1, 2, 3].map((i) => (
                <Skeleton key={i} variant="circular" width={30} height={30} />
              ))
            : socialLinks
                .filter((item) => item.url)
                .map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <IconButton
                      key={index}
                      size="small"
                      onClick={() => openLink(item.url)}
                      sx={{
                        bgcolor: "#FEB821",
                        color: "#fff",
                        p: 0.7,
                        "&:hover": {
                          bgcolor: "#FEB821",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <Icon fontSize="small" />
                    </IconButton>
                  );
                })}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: { xs: 1.5, sm: 3 },
          mt: 0.5,
        }}
      >
        {loading ? (
          <>
            <Skeleton height={20} />
            <Skeleton height={20} />
          </>
        ) : (
          <>
            <InfoItem icon={<MailIcon />} text={maskEmail(vendor?.email || "info@email.com")} />
            <InfoItem icon={<LanguageIcon />} text={vendor?.website || "website.com"} />
          </>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1,
          mt: 1,
        }}
      >
        {loading ? (
          <>
            <Skeleton height={18} />
            <Skeleton height={18} />
          </>
        ) : (
          <>
            <Typography fontSize={13} color="#707070">
              GSTIN: {vendor?.gst || "-"}
            </Typography>
            <Typography fontSize={13} color="#707070">
              Established: {vendor?.estimate_date || "-"}
            </Typography>
          </>
        )}
      </Box>
    </Box>

    {/* REVIEW SECTION */}
    <Box display="flex" width="100%">
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          borderColor: "#95B160",
          display: { xs: "none", lg: "block" },
          mx: 15,
        }}
      />

      <Box flex={1}>
        <Divider
          sx={{
            borderColor: "#95B160",
            mb: 2,
            display: { xs: "block", lg: "none" },
          }}
        />

        {loading ? (
          <>
            <Skeleton width={160} height={24} />
            <Skeleton width={200} height={40} />
          </>
        ) : (
          <>
            <Typography variant="h6" fontWeight={400} mb={1}>
              Give Your Rating
            </Typography>

           <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-start" },
              backgroundColor: "#FFFFFF",
              boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
              p: { xs: 1.2, sm: 1.5, md: 2 },   
              borderRadius: 2,
              width: "fit-content",
            }}
          >
         

            <Rating
            value={cardRating}
            onChange={() => {
              setUserRating(0);       
              handleOpenReviewPopup(); 
            }}
            icon={<BorderedStar />}
            emptyIcon={<BorderedStar sx={{ opacity: 0.25 }} />}
          />

                         </Box>
          </>
        )}
      </Box>
    </Box>
  </CardContent>
</Card>

      <Box sx={{ mt: 8, mb: 2 }}>
        <Paper sx={{ width: "100%", overflow: "hidden", mb: 2 }}>
          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              px: 2,
              mt: 2,
              "& .MuiTabs-flexContainer": { gap: 1 },
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            {tabs.map((label, i) => {
              const colors = {
                "Product List": "#608900",
                "Brand": "#608900",
                "Service": "#608900",
                "About Shop": "#608900",
                "Rating & Review": "#608900",
                "Photos": "#608900",
              };

              return (
                <Tab
                  key={label}
                  label={label}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    minHeight: 46,
                    px: { xs: 2, sm: 3 },
                    borderRadius: 1.5,
                    bgcolor: i === tab ? "#FEB821" : (colors[label] || "#95B160"),
                    color: i === tab ? "#000" : "#fff",
                    whiteSpace: "nowrap",
                    "&.Mui-selected": {
                      color: i === tab ? "#000" : "#fff",
                    },
                  }}
                />
              );
            })}
          </Tabs>

          {/* Content */}
          <Box p={{ xs: 2, sm: 3 }} maxHeight={{ xs: "auto", md: "70vh" }} overflow="auto">
            {currentTabLabel === "Product List" && (
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                  },
                }}
              >
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                  ))
                  : activeMaterials.map((item) => {
                    let images = [];
                    const material = item?.material;

                    if (material?.gallery) {
                      images = material.gallery
                        .split(",")
                        .map((img) =>
                          img.startsWith("http")
                            ? img
                            : `${ASSET_BASE}${img}`
                        )
                        .filter((img) => !img.endsWith(".mp4"));
                    } else if (Array.isArray(material?.image)) {
                      images = material.image;
                    }

if (images.length === 0) {
  images = ["/assets/images/placeholder_img.png"];
}

                    return (
                      <ProductShopCard
                        key={item.id}
                        id={item.id}
                        userid={item.user_id}
                        name={material?.material_name}
                        // image={img}
                        image={images}
                        description={material?.description}
                        materialid={material?.id}
                        brand={material?.brand?.[0]?.name}
                      />
                    );
                  })}
              </Box>
            )}

            {currentTabLabel === "Brand" && (
              <Box p={2}>
                <Typography variant="h6" mb={3} fontWeight={600}>
                  Brands
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(3, 1fr)",
                      md: "repeat(4, 1fr)",
                      lg: "repeat(6, 1fr)",
                    },
                    gap: 3,
                  }}
                >
                  {brands.map((brand) => (
                    <Box
                      key={brand.id}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: 1,
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "#FBFAF7",
                        overflow: "hidden",
                        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
                        "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.12)" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#fff",
                          borderRadius: "50%",
                          overflow: "hidden",
                          border: "1px solid #FEB821",
                        }}
                      >
                        <Box
                          component="img"
                          src={brand.logo || "/assets/images/placeholder_img.png"}
                          alt={brand.name}
                          sx={{
                            width: "70%",
                            height: "70%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                      <Typography fontSize={14} fontWeight={600}>
                        {brand.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {currentTabLabel === "Service" && (
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                  },
                }}
              >
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                  ))
                  : activeServices.map((item) => {
                    let serviceImages = [];
                    const service = item?.service;

                    if (service?.gallery) {
                      serviceImages = service.gallery
                        .split(",")
                        .map((img) =>
                          img.startsWith("http")
                            ? img
                            : `${ASSET_BASE}${img}`
                        )
                        .filter((img) => !img.endsWith(".mp4"));
                    } else if (Array.isArray(service?.image)) {
                      serviceImages = service.image;
                    }

                    if (serviceImages.length === 0) {
                      serviceImages = ["/assets/images/placeholder_img.png"];
                    }

                    return (
                      <MaterialService
                        key={item?.id}
                        name={service?.service_name}
                        image={serviceImages}
                        description={service?.description}
                        userid={item?.user_id}
                        serviceid={service?.id}
                        id={item?.id}
                        brand={service?.brand?.[0]?.name}
                      />
                    );
                  })}
              </Box>
            )}

            {currentTabLabel === "About Shop" && (
              <AboutShop
                name={vendor?.name}
                description={vendor?.description}
                phone={vendor?.phone}
                email={vendor?.email}
                website={vendor?.website}
                whatsapp={vendor?.whatsapp}
                address={vendor?.address}
                fax={vendor?.fax}
                taxnumber={vendor?.tax_number}
                established={vendor?.estimate_date}
              />
            )}

            {currentTabLabel === "Rating & Review" && (
              <ReviewRating
                rating={Number(vendor?.reviews_avg_rating || 0)}
                reviewCount={vendor?.reviews_count || 0}
                reviews={vendor?.reviews || []}
              />
            )}

           {currentTabLabel === "Photos" && (
  <MaterialPhotos
    vendorImages={vendor?.image || []}
    materialImages={[
      ...(activeServices?.flatMap((item) => {
        const service = item?.service;

        // If backend gives array (your API does this)
        if (Array.isArray(service?.image)) {
          return service.image;
        }

        return [];
      }) || [])
    ]}
  />
)}
          </Box>
        </Paper>
      </Box>

      {/* Delayed Enquiry Dialouge */}
      {enquiryContext && (
        <ProductQuoteDialouge
          open={enquiryOpen}
          onClose={() => setEnquiryOpen(false)}
          id={enquiryContext.id}
          name={enquiryContext.name}
          image={enquiryContext.image}
          description={enquiryContext.description}
          userid={vendor?.user_id}
          materialid={enquiryContext.id}
        />
      )}

    

<Dialog
  open={open}
  fullWidth
  maxWidth="xs"
  scroll="body"
  onClose={(e, reason) => {
    if (reason === "backdropClick") return;
    if (reason === "escapeKeyDown") return;
    handleClose();
  }}
  PaperProps={{
    sx: {
      background: "transparent",
      boxShadow: "none",
    },
  }}
>
  <DialogContent
    sx={{
      p: { xs: 1, sm: 2 },
      position: "relative", 
      background: "#fff",
      borderRadius: 3,
    }}
  >
    <IconButton
      onClick={handleClose}
      sx={{
        position: "absolute",
        top: 12,
        right: 12,
        width: 40,
        height: 40,
        color: "#7E9F40",
        zIndex: 10,
        "&:hover": {
         color: "#7E9F40",
        },
      }}
    >
      <CloseIcon />
    </IconButton>
   <Box
  sx={{
    display: "flex",
    justifyContent: "center",   
    mt: 3,
  }}
>
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2,
      width: 280,             
      textAlign: "left",
    }}
  >
    <Typography variant="h6" fontWeight={400}>
      Give Your Rating
    </Typography>


    <Rating
  value={userRating}
  onChange={(e, v) => setUserRating(v)}
  icon={<RatingStar />}
  emptyIcon={<RatingStar sx={{ opacity: 0.25 }} />}
/>


    <TextField
      multiline
      rows={2}
       column={6}
      sx={{width:250 }}
      placeholder="write Your Review"
      value={ratingDescription}
      onChange={(e) => setRatingDescription(e.target.value)}
      fullWidth
    />

    <Button
      variant="contained"
      sx={{
        bgcolor: "#4CA626C2",
        px:11,
        textTransform: "capitalize",
        alignSelf: "flex-start",
        fontWeight:400,
        fontSize:"20px"
      }}
      onClick={handleQuery}
    >
    Review
    </Button>
  </Box>
</Box>


  </DialogContent>
</Dialog>
    </Container>
  );
}

/* SMALL COMPONENTS */
const InfoItem = ({ icon, text }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Box sx={infoIcon}>{icon}</Box>
    <Typography fontSize={13} color="#6BAF4B" noWrap>
      {text}
    </Typography>
  </Box>
);

/*STYLES  */

const iconStyle = {
  fontSize: 32,
  p: 0.8,
  border: "1px solid #c1c1c1",
  borderRadius: "50%",
  cursor: "pointer",
  background: "#F5F5F5",
  color: "#6BAF4B",
};

const infoIcon = {
  borderRadius: "50%",
  p: 0.4,
  background: "#F5F5F5",
  boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  color: "#95B160",
};

const verifiedStyle = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  border: "1px solid #c1c1c1",
  borderRadius: "50px",
};

const btnYellow = {
  background: "#FEB821",
  color: "#fff",
  textTransform: "none",
  "&:hover": { background: "#FEB821" },
};

const btnGreen = {
  background: "#7E9F40",
  color: "#fff",
  textTransform: "none",
  "&:hover": { background: "#7E9F40" },
};